import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { QuestionnaireSourceAnswerDetail } from '@/contracts/customer-application-binding'
import { useAuth } from '@/lib/auth'
import {
  type CustomerApplication,
  type QuestionnaireAnswer,
  isReviewableApplicationStatus,
} from '@/lib/business-workflow'
import { listQuestionnaireSourceRecords } from '@/lib/customer-application-binding'
import { useBusinessWorkflowDataClientHydration } from '@/lib/data-client/use-business-workflow-hydration'
import {
  type DirectReviewLinkPayload,
  DirectReviewLinkTokenError,
  buildDirectReviewApprovePath,
  parseDirectReviewPayloadFromSearch,
  resolveDirectReviewApplication,
  resolveDirectReviewSourceRecord,
} from '@/lib/direct-review-links'
import { loadFixture } from '@/lib/mock-api/persistent-store'
import { useBusinessWorkflowStore } from '@/stores/businessWorkflowStore'
import type { Question } from '@/types/form'
import { ArrowRight, Building2, CheckCircle2, FileText, XCircle } from 'lucide-react'
import { useState } from 'react'

type QuestionnaireAnswerRow = {
  id: string
  groupTitle: string
  questionTitle: string
  answer: string
}

export default function DirectReviewQuestionnaireRoute() {
  const { isLoading: isAuthLoading } = useAuth()
  const workflowQuery = useBusinessWorkflowDataClientHydration(!isAuthLoading)
  const applications = useBusinessWorkflowStore((state) => state.applications)
  const salesUsers = useBusinessWorkflowStore((state) => state.salesUsers)
  const sourceLinks = useBusinessWorkflowStore((state) => state.sourceLinks)
  const fieldBindings = useBusinessWorkflowStore((state) => state.fieldBindings)
  const updateApplicationStatus = useBusinessWorkflowStore((state) => state.updateApplicationStatus)
  const [actionMessage, setActionMessage] = useState('')
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)

  const resolved = resolveDirectQuestionnaireState({
    applications,
    salesUsers,
    sourceLinks,
    fieldBindings,
  })

  const isHydrating = isAuthLoading || workflowQuery.isLoading || workflowQuery.isFetching
  if (resolved.kind === 'error' && isHydrating) {
    return (
      <main className="grid min-h-dvh place-items-center bg-slate-50 p-6 text-center text-sm text-slate-600">
        正在讀取問卷填寫內容...
      </main>
    )
  }

  if (resolved.kind === 'error') return <DirectReviewErrorPage message={resolved.message} />

  const ready = resolved
  const approvePath = buildDirectReviewApprovePath(ready.payload)
  const title = ready.application?.formTitle ?? ready.sourceRecord?.formTitle ?? '問卷填寫記錄'
  const respondentEmail =
    ready.application?.contactEmail ?? ready.sourceRecord?.submittedByEmail ?? ready.payload.email
  const submittedAt = ready.application?.submittedAt ?? ready.sourceRecord?.submittedAt ?? '未提供'
  const answers = buildQuestionnaireAnswerRows({
    formDetail: ready.formDetail,
    application: ready.application,
    sourceAnswers: ready.sourceRecord?.answerDetails ?? [],
  })
  const groupedAnswers = groupQuestionnaireAnswerRows(answers)
  const canReview = Boolean(
    ready.application && isReviewableApplicationStatus(ready.application.status),
  )
  const actionMessageKind = actionMessage.includes('已拒絕') ? 'success' : 'error'
  const statusLabel = ready.application
    ? getReviewStatusLabel(ready.application.status)
    : '僅查看填寫內容'

  async function rejectApplication() {
    if (!ready.application || !canReview) return
    setIsRejecting(true)
    try {
      await updateApplicationStatus(
        ready.application.id,
        'rejected',
        {
          email: ready.application.supervisorEmail,
          name: ready.application.supervisorName,
        },
        '直接問卷內容頁拒絕：審核者在完整問卷內容頁執行拒絕。',
      )
      setActionMessage('已拒絕此申請，審核紀錄已保留。')
      setRejectConfirmOpen(false)
    } catch (error) {
      setActionMessage(error instanceof Error ? error.message : '拒絕申請目前無法完成。')
    } finally {
      setIsRejecting(false)
    }
  }

  return (
    <main className="min-h-dvh bg-slate-50 text-slate-950">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-5 px-4 py-6 sm:px-6 lg:px-8">
        <header className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-[#4a2f21]/20 bg-[#4a2f21] px-5 py-5 text-white sm:px-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className="bg-white/15 text-white ring-1 ring-white/25">
                    審核資料檢視
                  </Badge>
                  <Badge
                    variant={canReview ? 'warning' : 'secondary'}
                    className="bg-white/15 text-white ring-1 ring-white/25"
                  >
                    {statusLabel}
                  </Badge>
                </div>
                <h1 className="mt-3 text-2xl font-semibold tracking-normal sm:text-3xl">
                  問卷填寫內容
                </h1>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-200">
                  先核對客戶摘要與這條填寫路線中已填寫的題目，確認無誤後再執行審核動作。
                </p>
              </div>
            </div>
          </div>
          {actionMessage ? (
            <div
              className={
                actionMessageKind === 'success'
                  ? 'm-5 rounded-md border border-[#4a2f21]/20 bg-[#4a2f21]/5 px-3 py-2 text-sm text-[#4a2f21]'
                  : 'm-5 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700'
              }
            >
              {actionMessage}
            </div>
          ) : null}
        </header>

        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
          <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div className="flex items-center gap-2">
                <FileText className="size-4 text-[#4a2f21]" />
                <div>
                  <h2 className="text-base font-semibold">填答明細</h2>
                  <p className="mt-0.5 text-xs text-slate-500">
                    只列出本次路線中已填寫的題目，方便逐段核對客戶資料。
                  </p>
                </div>
              </div>
              <Badge variant="outline">共 {answers.length} 題</Badge>
            </div>
            <div className="flex flex-col gap-5 p-5">
              {groupedAnswers.length ? (
                groupedAnswers.map((group) => (
                  <AnswerGroupTable
                    key={group.title || 'questionnaire-answers'}
                    title={group.title}
                    answers={group.answers}
                  />
                ))
              ) : (
                <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
                  目前沒有可顯示的填答明細。
                </div>
              )}
            </div>
          </section>

          <aside className="lg:sticky lg:top-6 lg:self-start">
            <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-2">
                <Building2 className="size-4 text-[#4a2f21]" />
                <h2 className="text-base font-semibold">審核操作</h2>
              </div>
              <div className="mt-4 space-y-3 text-sm">
                <ActionSummary label="問卷" value={title} />
                <ActionSummary label="客戶" value={ready.application?.companyName ?? '-'} />
                <ActionSummary label="目前狀態" value={statusLabel} />
                <ActionSummary label="填寫人 Email" value={respondentEmail} />
                <ActionSummary label="送出時間" value={submittedAt} />
              </div>
              <div className="mt-5 grid gap-2">
                {canReview ? (
                  <>
                    <Button
                      asChild
                      size="lg"
                      className="h-12 rounded-lg bg-[#4a2f21] text-base font-semibold text-white shadow-lg shadow-[#4a2f21]/15 hover:bg-[#3b251a] focus-visible:ring-[#4a2f21]"
                    >
                      <a
                        className="text-white visited:text-white hover:text-white"
                        href={approvePath}
                      >
                        <CheckCircle2 className="size-4" />
                        直接審核通過
                        <ArrowRight className="size-4" />
                      </a>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="lg"
                      className="h-12 rounded-lg border-[#4a2f21]/30 text-[#4a2f21] hover:bg-[#4a2f21]/5"
                      onClick={() => setRejectConfirmOpen(true)}
                    >
                      <XCircle className="size-4" />
                      直接拒絕
                    </Button>
                  </>
                ) : (
                  <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm leading-6 text-slate-600">
                    <p>此記錄已處理完成，因此此頁只提供資料檢視。</p>
                    <Button asChild variant="outline" className="w-full">
                      <a href="/dashboard/reviews">返回管理中心</a>
                    </Button>
                  </div>
                )}
              </div>
            </section>
          </aside>
        </div>
      </div>
      <RejectConfirmDialog
        open={rejectConfirmOpen}
        isSubmitting={isRejecting}
        onOpenChange={(open) => {
          if (!isRejecting) setRejectConfirmOpen(open)
        }}
        onConfirm={() => void rejectApplication()}
      />
    </main>
  )
}

function RejectConfirmDialog({
  open,
  isSubmitting,
  onOpenChange,
  onConfirm,
}: {
  open: boolean
  isSubmitting: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>確認拒絕此申請？</DialogTitle>
          <DialogDescription>
            拒絕後會保留此筆問卷與審核紀錄，之後可在管理中心追蹤狀態。
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            取消
          </Button>
          <Button
            className="bg-[#4a2f21] text-white hover:bg-[#3b251a]"
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? '處理中...' : '確認拒絕'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function resolveDirectQuestionnaireState(input: {
  applications: CustomerApplication[]
  salesUsers: ReturnType<typeof useBusinessWorkflowStore.getState>['salesUsers']
  sourceLinks: ReturnType<typeof useBusinessWorkflowStore.getState>['sourceLinks']
  fieldBindings: ReturnType<typeof useBusinessWorkflowStore.getState>['fieldBindings']
}):
  | {
      kind: 'ready'
      payload: DirectReviewLinkPayload
      application?: CustomerApplication
      sourceRecord?: ReturnType<typeof resolveDirectReviewSourceRecord>
      formDetail?: ReturnType<typeof loadFixture>['formDetails'][string]
    }
  | { kind: 'error'; message: string } {
  try {
    const payload = parseDirectReviewPayloadFromSearch(window.location.search)
    const snapshot = loadFixture()
    const sourceRecords = listQuestionnaireSourceRecords({
      forms: snapshot.forms,
      responses: snapshot.responses,
      sourceLinks: input.sourceLinks,
      applications: input.applications,
      salesUsers: input.salesUsers,
      formDetails: snapshot.formDetails,
      fieldBindings: input.fieldBindings,
    })
    const application = resolveDirectReviewApplication(input.applications, payload)
    const sourceRecord = resolveDirectReviewSourceRecord(sourceRecords, payload)
    if (!application && !sourceRecord) {
      return { kind: 'error', message: '找不到符合此加密參數的問卷填寫記錄。' }
    }
    return {
      kind: 'ready',
      payload,
      application,
      sourceRecord,
      formDetail: snapshot.formDetails[payload.formId],
    }
  } catch (error) {
    return {
      kind: 'error',
      message:
        error instanceof DirectReviewLinkTokenError
          ? error.message
          : '直接查看問卷內容連結目前無法使用。',
    }
  }
}

function buildQuestionnaireAnswerRows({
  formDetail,
  application,
  sourceAnswers,
}: {
  formDetail?: ReturnType<typeof loadFixture>['formDetails'][string]
  application?: CustomerApplication
  sourceAnswers: QuestionnaireSourceAnswerDetail[]
}): QuestionnaireAnswerRow[] {
  if (formDetail?.questions.length) {
    return buildRowsFromFormQuestions(
      formDetail.questions,
      formDetail.groups,
      sourceAnswers,
      application?.answers ?? [],
    )
  }
  if (sourceAnswers.length > 0) return sourceAnswersToRows(sourceAnswers)
  return applicationAnswersToRows(application?.answers ?? [])
}

function buildRowsFromFormQuestions(
  questions: Question[],
  groups: Array<{ id: string; title: string }>,
  sourceAnswers: QuestionnaireSourceAnswerDetail[],
  applicationAnswers: QuestionnaireAnswer[],
) {
  const groupTitleById = new Map(groups.map((group) => [group.id, group.title]))
  const sourceByQuestionId = new Map(sourceAnswers.map((answer) => [answer.questionId, answer]))
  const applicationByTitle = new Map(
    applicationAnswers.map((answer) => [answer.questionTitle, answer]),
  )

  return questions
    .map((question) => {
      const sourceAnswer = sourceByQuestionId.get(question.id)
      const applicationAnswer = applicationByTitle.get(question.title)
      return {
        id: question.id,
        groupTitle: question.groupId
          ? (groupTitleById.get(question.groupId) ?? question.groupId)
          : '',
        questionTitle: question.title,
        answer: sourceAnswer?.valuePreview ?? applicationAnswer?.answer ?? '',
      }
    })
    .filter((row) => hasQuestionnaireAnswer(row.answer))
}

function applicationAnswersToRows(answers: QuestionnaireAnswer[]): QuestionnaireAnswerRow[] {
  return answers
    .map((answer) => ({
      id: answer.id,
      groupTitle: '',
      questionTitle: answer.questionTitle,
      answer: answer.answer,
    }))
    .filter((row) => hasQuestionnaireAnswer(row.answer))
}

function sourceAnswersToRows(answers: QuestionnaireSourceAnswerDetail[]): QuestionnaireAnswerRow[] {
  return answers
    .map((answer) => ({
      id: answer.questionId,
      groupTitle: '',
      questionTitle: answer.questionTitle,
      answer: answer.valuePreview,
    }))
    .filter((row) => hasQuestionnaireAnswer(row.answer))
}

function hasQuestionnaireAnswer(value: string) {
  return value.trim() !== ''
}

function groupQuestionnaireAnswerRows(rows: QuestionnaireAnswerRow[]) {
  const groups = new Map<string, QuestionnaireAnswerRow[]>()
  for (const row of rows) {
    const title = row.groupTitle || ''
    groups.set(title, [...(groups.get(title) ?? []), row])
  }
  return Array.from(groups.entries()).map(([title, answers]) => ({ title, answers }))
}

function getReviewStatusLabel(status: CustomerApplication['status']) {
  const labels: Record<CustomerApplication['status'], string> = {
    draft: '草稿',
    pending_review: '待審核',
    changes_requested: '需補件',
    approved: '已核准',
    approved_pending_erp_filing: '已核准，待 ERP 建檔',
    customer_created: '客戶已建檔',
    duplicate_pending: '疑似重複待處理',
    rejected: '已拒絕',
  }
  return labels[status] ?? status
}

function ActionSummary({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-[#4a2f21]/10 bg-[#4a2f21]/5 px-3 py-2">
      <div className="text-xs font-medium text-[#4a2f21]/75">{label}</div>
      <div className="mt-1 break-words font-medium text-slate-900">{value || '-'}</div>
    </div>
  )
}

function AnswerGroupTable({
  title,
  answers,
}: {
  title: string
  answers: QuestionnaireAnswerRow[]
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      {title ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-[#4a2f21]/5 px-4 py-3">
          <h3 className="font-semibold text-[#4a2f21]">{title}</h3>
          <Badge variant="outline" className="border-[#4a2f21]/20 text-[#4a2f21]">
            {answers.length} 題
          </Badge>
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <table
          className="w-full min-w-[560px] border-collapse text-sm"
          aria-label={title ? `${title}填答明細` : '填答明細'}
        >
          <thead className="bg-[#4a2f21]/5 text-left text-xs font-semibold text-[#4a2f21]">
            <tr>
              <th className="w-[34%] border-b border-[#4a2f21]/10 px-4 py-3">問卷題目</th>
              <th className="border-b border-[#4a2f21]/10 px-4 py-3">題目回答</th>
            </tr>
          </thead>
          <tbody>
            {answers.map((answer) => (
              <tr key={answer.id} className="align-top">
                <th className="border-b border-slate-100 bg-[#4a2f21]/[0.03] px-4 py-3 text-left font-semibold text-slate-950">
                  {answer.questionTitle}
                </th>
                <td className="whitespace-pre-wrap border-b border-slate-100 px-4 py-3 leading-6 text-slate-800">
                  {answer.answer || '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function DirectReviewErrorPage({ message }: { message: string }) {
  return (
    <main className="grid min-h-dvh place-items-center bg-slate-50 p-6 text-center">
      <div className="max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <Badge variant="destructive">連結無法使用</Badge>
        <h1 className="mt-4 text-xl font-semibold">無法開啟問卷填寫內容</h1>
        <p className="mt-2 text-sm text-slate-600">{message}</p>
      </div>
    </main>
  )
}
